import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import type { SupaSQBuilding, SupaSQUnit } from '../../types/supa';
import { supaBuildingKey } from '../../types/supa';

const UNIT_STATUS: Record<string, { label: string; color: 'success' | 'primary' | 'warning' | 'default' }> = {
  '300': { label: 'Available',            color: 'success' },
  '301': { label: 'Connected with Sonic', color: 'primary' },
  '302': { label: 'Transfer Available',   color: 'warning' },
};

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ bgcolor: '#1e3a5c', px: 2, py: 0.75, mx: -3, mt: -3, mb: 2.5, borderRadius: '4px 4px 0 0' }}>
      <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: '#ffffff' }}>
        {children}
      </Typography>
    </Box>
  );
}

function buildingLabel(b: SupaSQBuilding): string {
  return [
    b.buildingName,
    b.address_street_number && b.address_route
      ? `${b.address_street_number} ${b.address_route}`
      : b.address_route,
    b.address_locality,
    b.address_administrative_area_level_1,
    b.address_postal_code,
  ].filter(Boolean).join(', ');
}

function unitLabel(u: SupaSQUnit): string {
  return u.unitNo ? `Unit ${u.unitNo}` : u.addressLine1 || u.rid;
}

function formatUnitAddress(u: SupaSQUnit): string {
  return [
    u.unitNo ? `Unit ${u.unitNo}` : null,
    u.addressLine1,
    u.city,
    u.state,
    u.postcode,
  ].filter(Boolean).join(', ');
}

type SQState =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'units'; units: SupaSQUnit[] }
  | { phase: 'building_only' }
  | { phase: 'not_serviceable'; description: string }
  | { phase: 'error'; message: string };

export default function SupaServiceQualificationPage() {
  const navigate = useNavigate();

  const [buildings, setBuildings] = useState<SupaSQBuilding[]>([]);
  const [buildingsLoading, setBuildingsLoading] = useState(true);
  const [buildingsError, setBuildingsError] = useState<string | null>(null);

  const [selectedBuilding, setSelectedBuilding] = useState<SupaSQBuilding | null>(null);
  const [sq, setSq] = useState<SQState>({ phase: 'idle' });
  const [selectedUnit, setSelectedUnit] = useState<SupaSQUnit | null>(null);
  const [autocompleteKey, setAutocompleteKey] = useState(0);

  useEffect(() => {
    api.adminSupaServicedBuildings()
      .then((data) => {
        setBuildings(data.servicedBuildings ?? []);
        setBuildingsLoading(false);
      })
      .catch(() => {
        setBuildingsError('Could not load serviced buildings list');
        setBuildingsLoading(false);
      });
  }, []);

  const handleBuildingSelect = async (b: SupaSQBuilding) => {
    setSelectedBuilding(b);
    setSelectedUnit(null);
    setSq({ phase: 'loading' });
    try {
      const data = await api.adminSupaServiceQualification({
        StreetName: b.address_route,
        StreetNo: b.address_street_number || undefined,
        Suburb: b.address_locality,
        State: b.address_administrative_area_level_1 || undefined,
        Postcode: b.address_postal_code || undefined,
      });
      // SQ is queried by street address, and multiple buildings in the same complex can
      // share one street address — it returns units for the whole complex, so filter
      // down to the specific building selected.
      const units = (data.buildingUnits ?? []).filter((u) => u.buildingName === b.buildingName);
      if (data.statusCode === '200' && units.length > 0) {
        setSq({ phase: 'units', units });
      } else if (data.statusCode === '200') {
        setSq({ phase: 'building_only' });
      } else if (data.statusCode === '402' && units.length > 0) {
        setSq({ phase: 'units', units });
      } else {
        setSq({ phase: 'not_serviceable', description: data.description || 'Address is not on the Supa Networks network.' });
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setSq({ phase: 'error', message: err?.response?.data?.detail || err?.message || 'Service qualification check failed' });
    }
  };

  const handleReset = () => {
    setSelectedBuilding(null);
    setSelectedUnit(null);
    setSq({ phase: 'idle' });
    setAutocompleteKey((k) => k + 1);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        Service Qualification
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Check if an address is serviceable on the Supa Networks network — search for the building, then select the unit.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <SectionLabel>Building</SectionLabel>

        {!selectedBuilding && (
          buildingsError ? (
            <Alert severity="warning">
              {buildingsError} —{' '}
              <Button size="small" onClick={() => { setBuildingsError(null); setBuildingsLoading(true); api.adminSupaServicedBuildings().then((data) => { setBuildings(data.servicedBuildings ?? []); setBuildingsLoading(false); }).catch(() => { setBuildingsError('Could not load serviced buildings list'); setBuildingsLoading(false); }); }}>
                Retry
              </Button>
            </Alert>
          ) : (
            <Autocomplete
              key={autocompleteKey}
              options={buildings}
              loading={buildingsLoading}
              filterOptions={(options, state) => {
                const q = state.inputValue.toLowerCase().trim();
                if (!q) return options.slice(0, 100);
                const results = options.filter((b) => {
                  const haystack = [
                    b.buildingName,
                    b.address_subpremise,
                    b.address_street_number,
                    b.address_route,
                    `${b.address_street_number ?? ''} ${b.address_route ?? ''}`.trim(),
                    b.address_locality,
                    b.address_postal_code,
                  ].filter(Boolean).join(' ').toLowerCase();
                  return haystack.includes(q);
                });
                return results.slice(0, 30);
              }}
              getOptionLabel={buildingLabel}
              onChange={(_, b) => { if (b) handleBuildingSelect(b); }}
              noOptionsText={buildingsLoading ? 'Loading…' : 'No matching addresses — try a street name or suburb'}
              renderOption={(props, b) => (
                <li {...props} key={supaBuildingKey(b)}>
                  <Box sx={{ py: 0.25 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {b.buildingName.replace(/[\r\n]+/g, ' — ')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[b.address_street_number, b.address_route, b.address_locality, b.address_administrative_area_level_1, b.address_postal_code].filter(Boolean).join(' ')}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Search your building"
                  placeholder="Type a building name, street or suburb…"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {buildingsLoading && <CircularProgress size={16} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )
        )}

        {selectedBuilding && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
            <Box>
              <Typography variant="body2" fontWeight={600}>{selectedBuilding.buildingName}</Typography>
              <Typography variant="caption" color="text.secondary">
                {[selectedBuilding.address_street_number, selectedBuilding.address_route, selectedBuilding.address_locality, selectedBuilding.address_administrative_area_level_1, selectedBuilding.address_postal_code].filter(Boolean).join(' ')}
              </Typography>
            </Box>
            <Button size="small" onClick={handleReset} sx={{ flexShrink: 0 }}>Change</Button>
          </Box>
        )}
      </Paper>

      {sq.phase === 'loading' && (
        <Paper sx={{ p: 3, mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Checking availability…</Typography>
        </Paper>
      )}

      {sq.phase === 'not_serviceable' && (
        <Alert severity="error" icon={<CancelIcon fontSize="small" />} sx={{ mb: 3 }}>
          {sq.description}
        </Alert>
      )}

      {sq.phase === 'error' && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {sq.message}
        </Alert>
      )}

      {sq.phase === 'building_only' && (
        <Alert severity="success" icon={<CheckCircleIcon fontSize="small" />} sx={{ mb: 3 }}>
          This building is serviceable, but no individual unit list was returned.
        </Alert>
      )}

      {sq.phase === 'units' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <SectionLabel>Unit</SectionLabel>
          <Autocomplete
            options={sq.units}
            getOptionLabel={unitLabel}
            value={selectedUnit}
            onChange={(_, u) => setSelectedUnit(u)}
            renderOption={(props, u) => {
              const st = UNIT_STATUS[u.statusCode] ?? { label: u.statusCode, color: 'default' as const };
              return (
                <li {...props} key={u.rid}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                    <Typography variant="body2">{unitLabel(u)}</Typography>
                    <Chip label={st.label} color={st.color} size="small" />
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Select your unit / apartment"
                placeholder="Type or scroll to find the unit…"
              />
            )}
            noOptionsText="No units found for this building"
          />

          {selectedUnit && (
            <Box sx={{ mt: 2.5, pt: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">Address</Typography>
                  <Typography variant="body2" fontWeight={600}>{formatUnitAddress(selectedUnit)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">Status</Typography>
                  <Chip
                    label={(UNIT_STATUS[selectedUnit.statusCode] ?? { label: selectedUnit.statusCode }).label}
                    color={(UNIT_STATUS[selectedUnit.statusCode] ?? { color: 'default' as const }).color}
                    size="small"
                  />
                  {selectedUnit.statusDescription && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {selectedUnit.statusDescription}
                    </Typography>
                  )}
                </Grid>
                {selectedUnit.networkTechnology && (
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary" display="block">Network</Typography>
                    <Typography variant="body2">{selectedUnit.networkTechnology}</Typography>
                  </Grid>
                )}
                {selectedUnit.unitType && (
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary" display="block">Unit type</Typography>
                    <Typography variant="body2">{selectedUnit.unitType}</Typography>
                  </Grid>
                )}
                {selectedUnit.ipoeEnabled && (
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary" display="block">IPoE enabled</Typography>
                    <Typography variant="body2">{selectedUnit.ipoeEnabled}</Typography>
                  </Grid>
                )}
                {selectedUnit.remoteId && (
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary" display="block">BUID</Typography>
                    <Typography variant="body2">{selectedUnit.remoteId}</Typography>
                  </Grid>
                )}
                {selectedUnit.circuitId && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">Circuit ID</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{selectedUnit.circuitId}</Typography>
                  </Grid>
                )}
                {selectedUnit.serviceID && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">Service ID</Typography>
                    <Typography variant="body2">{selectedUnit.serviceID}</Typography>
                  </Grid>
                )}
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">RID</Typography>
                  <Typography variant="body2">{selectedUnit.rid}</Typography>
                </Grid>
              </Grid>

              <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/admin/orders', {
                    state: {
                      autoOpenNewOrder: true,
                      prefillServiceAddress: formatUnitAddress(selectedUnit),
                    },
                  })}
                >
                  Create Order
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}
